/**
 * User Preferences Types
 *
 * Types for user preferences error handling and validation.
 * These types are used by the UserPreferencesService for error classification
 * and providing user-friendly error messages.
 *
 * Also includes user exercise preference types for managing per-exercise
 * tracking settings (e.g., weight & reps, time-based, distance-based).
 *
 * @module types/domain/user-preferences
 */

import type { API_ERROR_CODES, ValidationErrorInfo } from '../api/errors';
import type { ID } from '../common/id.types';

// =============================================================================
// ERROR CODE TYPES
// =============================================================================

/**
 * Error codes specific to user preferences operations
 *
 * These codes map to specific HTTP status codes and error conditions
 * that can occur during preference update operations.
 *
 * @example
 * ```typescript
 * const handleError = (code: UserPreferencesErrorCode) => {
 *   switch (code) {
 *     case API_ERROR_CODES.UNAUTHENTICATED:
 *       return 'Please log in to update preferences';
 *     case API_ERROR_CODES.VALIDATION_ERROR:
 *       return 'Please check your input values';
 *     default:
 *       return 'An error occurred';
 *   }
 * };
 * ```
 */
export type UserPreferencesErrorCode =
  | typeof API_ERROR_CODES.UNAUTHENTICATED
  | typeof API_ERROR_CODES.FORBIDDEN
  | typeof API_ERROR_CODES.VALIDATION_ERROR
  | typeof API_ERROR_CODES.SERVER_ERROR
  | typeof API_ERROR_CODES.NETWORK_ERROR
  | typeof API_ERROR_CODES.TIMEOUT_ERROR
  | typeof API_ERROR_CODES.UNKNOWN_ERROR;

// =============================================================================
// ERROR INFO TYPES
// =============================================================================

/**
 * Error info returned from preference update operations
 *
 * Provides structured error information including the error code,
 * technical and user-friendly messages, retry eligibility, and
 * optional validation errors for form feedback.
 *
 * @example
 * ```typescript
 * const errorInfo: UserPreferencesErrorInfo = {
 *   code: API_ERROR_CODES.VALIDATION_ERROR,
 *   message: 'Validation failed for field: theme_selection',
 *   userMessage: 'Please check your input and try again.',
 *   isRetryable: false,
 *   validationErrors: [
 *     { field: 'theme_selection', message: 'Invalid theme value', rule: 'enum' }
 *   ]
 * };
 *
 * // Display to user
 * showError(errorInfo.userMessage);
 *
 * // Show field-specific errors
 * errorInfo.validationErrors?.forEach(error => {
 *   showFieldError(error.field, error.message);
 * });
 * ```
 */
export interface UserPreferencesErrorInfo {
  /** The specific error code for this error */
  code: UserPreferencesErrorCode;

  /** Technical error message (for logging/debugging) */
  message: string;

  /** User-friendly error message (for display) */
  userMessage: string;

  /** Whether this operation can be retried */
  isRetryable: boolean;

  /** Validation errors for specific fields (present for validation errors) */
  validationErrors?: ValidationErrorInfo[];
}

// =============================================================================
// USER EXERCISE PREFERENCE TYPES
// =============================================================================

/**
 * Valid tracking types for exercise preferences.
 *
 * Determines how an exercise's sets are tracked:
 * - 'Reps Only': Track number of repetitions only
 * - 'Weight & Reps': Track weight lifted and repetitions
 * - 'Time Based': Track duration of the exercise
 * - 'Distance Based': Track distance covered
 * - 'Time & Distance': Track both duration and distance
 * - 'Weight & Time': Track weight and duration (e.g., weighted planks)
 *
 * @example
 * ```typescript
 * const trackingType: ExercisePreferenceTrackingType = 'Weight & Reps';
 *
 * // Use in preference creation
 * const preference = {
 *   exercise_guid: 'abc-123',
 *   tracking_type: trackingType,
 * };
 * ```
 */
export type ExercisePreferenceTrackingType =
  | 'Reps Only'
  | 'Weight & Reps'
  | 'Time Based'
  | 'Distance Based'
  | 'Time & Distance'
  | 'Weight & Time';

/**
 * Array of all valid tracking types for validation
 */
export const EXERCISE_PREFERENCE_TRACKING_TYPES: readonly ExercisePreferenceTrackingType[] =
  [
    'Reps Only',
    'Weight & Reps',
    'Time Based',
    'Distance Based',
    'Time & Distance',
    'Weight & Time',
  ] as const;

/**
 * User's tracking preference for a specific exercise.
 *
 * Stores how a user prefers to track sets for a particular exercise.
 * The preference is tied to an exercise by its GUID and includes
 * timestamps for creation and last update.
 *
 * @example
 * ```typescript
 * const preference: UserExercisePreference = {
 *   exercise_guid: 'bench-press-guid',
 *   tracking_type: 'Weight & Reps',
 *   created_at: '2024-01-15 10:30:00',
 *   updated_at: '2024-01-15 10:30:00',
 * };
 *
 * // Display preference
 * console.log(`Exercise ${preference.exercise_guid} tracks: ${preference.tracking_type}`);
 * ```
 */
export interface UserExercisePreference {
  /** GUID of the exercise this preference applies to */
  exercise_guid: ID;

  /** How sets should be tracked for this exercise */
  tracking_type: ExercisePreferenceTrackingType;

  /** When the preference was created (YYYY-MM-DD HH:mm:ss) */
  created_at: string;

  /** When the preference was last updated (YYYY-MM-DD HH:mm:ss) */
  updated_at: string;
}

// =============================================================================
// USER EXERCISE PREFERENCE TYPE GUARDS
// =============================================================================

/**
 * Type guard to check if a value is a valid ExercisePreferenceTrackingType
 *
 * @param value - The value to check
 * @returns True if value is a valid ExercisePreferenceTrackingType
 *
 * @example
 * ```typescript
 * const input = 'Weight & Reps';
 * if (isExercisePreferenceTrackingType(input)) {
 *   // input is ExercisePreferenceTrackingType
 *   const preference: UserExercisePreference = {
 *     exercise_guid: 'abc',
 *     tracking_type: input,
 *     created_at: dayjs().toISOString(),
 *     updated_at: dayjs().toISOString(),
 *   };
 * }
 * ```
 */
export function isExercisePreferenceTrackingType(
  value: unknown
): value is ExercisePreferenceTrackingType {
  return (
    typeof value === 'string' &&
    EXERCISE_PREFERENCE_TRACKING_TYPES.includes(
      value as ExercisePreferenceTrackingType
    )
  );
}
